import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { activateTournamentFixture } from '@/api/tournament/tournamentApi';
import { handleGlobalError } from '@/utils/error-handler';

interface ActivateFixtureModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tournamentId: string;
  onActivated: () => void;
  /** Knockout-only: there is no group stage to announce, and no groups to swap teams between. */
  knockoutOnly?: boolean;
}

export function ActivateFixtureModal({
  open,
  onOpenChange,
  tournamentId,
  onActivated,
  knockoutOnly = false,
}: ActivateFixtureModalProps) {
  const [isActivating, setIsActivating] = useState(false);

  const handleActivateFixture = async () => {
    try {
      setIsActivating(true);
      await activateTournamentFixture(tournamentId);

      toast({
        title: 'Fixture activado',
        description: knockoutOnly
          ? 'El cuadro ahora es visible para los jugadores en la app.'
          : 'La fase de grupos ahora es visible para los jugadores en la app.',
        variant: 'default',
      });

      onOpenChange(false);
      onActivated();
    } catch (error) {
      console.error('Error al activar el fixture:', error);
      handleGlobalError(
        error,
        'Error',
        'No se pudo activar el fixture. Inténtalo de nuevo.',
      );
    } finally {
      setIsActivating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-600" />
            Activar fixture
          </DialogTitle>
          <DialogDescription>
            Una vez que actives el fixture, los jugadores podrán ver{' '}
            {knockoutOnly ? 'el cuadro' : 'la fase de grupos'} en la app. Podrás
            seguir ajustando horarios, pero se considerará la versión pública
            del fixture.
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          <div className="rounded-lg bg-emerald-50 p-4 text-sm text-emerald-900">
            <p className="font-medium">Confirmación requerida:</p>
            <ul className="mt-2 list-inside list-disc space-y-1">
              <li>
                Mientras el fixture está en modo borrador, solo vos lo ves como
                manager.
              </li>
              <li>
                Al activarlo, los jugadores verán los partidos y horarios
                actuales.
              </li>
              {!knockoutOnly && (
                <li>
                  Después de activarlo, solo vas a poder intercambiar entre
                  grupos a los equipos que todavía no jugaron.
                </li>
              )}
            </ul>
          </div>
        </div>

        <DialogFooter className="sm:justify-between">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isActivating}
          >
            Cancelar
          </Button>
          <Button onClick={handleActivateFixture} disabled={isActivating}>
            {isActivating ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Activando...
              </>
            ) : (
              'Activar fixture'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

